import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { isEqual, get, camelCase,set } from 'lodash';
import { createAction } from '@ngrx/store';
import { TranslatorService } from '@services/translator/translator.service';
import { ProfileService } from '@services/profile/profile.service';
import { buildScreenCtx, ScreenContext } from '@app/core/store';
import * as moment from 'moment';

/**
 * This utility service is injected in all the screens' components generated by UI Studio tool that provides the following utilities:
 * - access to some global services
 * - patches form's value
 * - navigates to nested routes
 * - downloads a file
 * - builds the context of the screens
 * - WCAG controls
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyFeatureService extends FeatureService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
   { provide: FeatureService, useClass: MyFeatureService }
  ]
 ```
 * */
@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  /**
   * The store variable of the screen's contexts
   */
  private context: Object = {};
  /**
   * The context's query parameter value
   */
  private ctxQueryParam: string;
  /**
   * The last focus element component
   */
  public lastFocusElement: any;

  /**
   * Default  Constructor of the component
   */
  constructor(
    private formBuilderServ: FormBuilder,
    private profileServ: ProfileService,
    private translatorServ: TranslatorService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this.activatedRoute.queryParams.subscribe(queryParams => {
      if (!queryParams['ctx']) {
        delete this.context['ctxQueryParam'];
      } else {
        this.ctxQueryParam = queryParams['ctx'];
      }
    });
  }
  /**
   * Get the profile manager service
   * @return {ProfileService}
   */
  get profileService(): ProfileService {
    return this.profileServ;
  }
  /**
   * Get the translator service
   * @return {TranslatorService}
   */
  get translatorService(): TranslatorService {
    return this.translatorServ;
  }
  /**
   * Get angular reactive form builder
   * @return {FormBuilder}
   */
  get formBuilder(): FormBuilder {
    return this.formBuilderServ;
  }
  /**
   * Validate the form fields
   * @param formGroup
   * @returns {void}
   */
  validateForm(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateForm(control);
      } else if (control instanceof FormArray) {
        (control as FormArray).controls.forEach((group) => this.validateForm(group as FormGroup));
      }
    });
  }
  /**
  * Dispatch an action to update the state of the caller
  * by the selected items
  @param selectedItems
  @param params
  @param parentCtx
  @param store
  @returns {void}
  */
  doSelect(selectedItems, params, parentCtx, store) {
    const selectionValue = selectedItems instanceof Array ? [...selectedItems] : selectedItems;
    const data = {
      ...params,
      targetCtx: { ...parentCtx },
      value: selectionValue,
      propertyToModify: parentCtx.roleName,
      overrideValue: true
    };
    const selectionActionNameByRole = parentCtx['ignoreDispatchingSelectionByRole']
      ? ''
      : ` ${parentCtx.roleName}`;
    const selectAction = createAction(
      `[${parentCtx.feature} ${parentCtx.screenType}] Select${selectionActionNameByRole}`,
      payload => payload
    );
    store.dispatch(selectAction(data));
  }

  /**
 * Navigate to the auxiliary outlets provided as parameter
 * It cleans the default outlets first: call and dialog
 * @param props
 * @param activeRoute
 * @param param
 */
  navigate(
    feature: string,
    screen: string,
    navigationType: string,
    param: string,
    activeRoute: ActivatedRoute
  ): void {
    this.navigateTo(
      {
        [navigationType]: null,
      },
      activeRoute,
      param,
      navigationType,
      feature,
      screen
    ).then(() => {
      this.navigateTo(
        {
          [navigationType]: [
            feature,
            screen,
            {
              screenId: param,
            },
          ],
        },
        activeRoute,
        param,
        navigationType,
        feature,
        screen
      );
    });
  }
  /**
   * Navigate to a target auxiliary router outlets
   * @param outlets
   * @param activeRoute
   */
  navigateTo(
    outlets: Object,
    activeRoute: ActivatedRoute,
    param: string,
    navigationType: string,
    feature: string,
    screen: string
  ): Promise<boolean> {
    return this.router.navigate(
      [
        navigationType === 'switch' ? `/${feature}/${screen}` : './',
        {
          outlets: {
            ...outlets,
          },
        },
      ],
      {
        relativeTo: activeRoute,
        queryParams: {
          fc: crypto.getRandomValues(new Uint32Array(1)),
          ctx: param,
        },
      }
    );
  }

  /**
   * Patches only the distincts form values with the provided data object as input param
   * @param data
   * @param form
   */
  patchValue(data: any, form: FormGroup) {
    Object.keys(data).forEach((key) => {
      const formControl = form.controls[key];
      if (formControl && (!formControl.value || !isEqual(data[key], formControl.value))) {
        form.controls[key].patchValue(data[key], {
          onlySelf: true,
          emitEvent: true,
        });
      }
    });
    form.updateValueAndValidity();
  }
  /**
   * Set the value of each field in the data
   * @param data
   * @param item
   * @param value
   * */
  setFieldValue(data: any, item: any, value: any) {
    if (item.indexOf('.') != -1) {
      let relationField = get(data, item.substr(0, item.indexOf('.')));
      data = set(data, item, get(relationField, item.split('.')[1]));
    } else {
      data = set(data, item, value[camelCase(item.split('.').join(' '))]);
    }
  }

  /**
   * Patches only the filtred value to avoid undefined
   * @param keys
   * @param form
   * @param data
   * */
  patchFiltersValue(keys: any, form: FormGroup, data: any) {
    keys.split(',').forEach((item) => {
      const result = get(data, item);
      if (result) {
        form.controls[camelCase(item.split('.').join(' '))].setValue(result);
      }
    });
  }

  /**   
   * Download a file in an iframe   
   * @param res   
   * @param fileName   
   */
   downloadFileInFrame(res: any, fileName: string) {
    const file = this.isBase64(res.body) ? this.convertBase64ToBlobData(res.body, fileName) : this.createFile({ body: res.body }, fileName);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(file);
    link.download = fileName; document.body.appendChild(link);
    link.click(); document.body.removeChild(link);
  }

  /**
   * Converts a base64 String to Blob
   * @param base64Data
   * @param fileName
   * @param sliceSize
   * @returns {File}
   */
   convertBase64ToBlobData(base64Data: string, fileName: string, sliceSize=512) {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: 'text/octet-stream' });
    return this.createFile({body: blob}, fileName);
  }

  /**
   * Checks whether a string is in base64 format or not
   * @param str
   * @returns
   */
  isBase64(str) {
    if (str === '' || str.trim() === '') { return false; }
    try {
      return btoa(atob(str)) == str;
    } catch (err) {
      return false;
    }
  }

  /**
   * Create a file for the provided binary content
   * @param res
   * @param uploadFileName
   *
   */
  createFile(res: any, uploadFileName: any): File {
    let file;
    if (!navigator.msSaveBlob) { // detect if not Edge
      file = new File(res ? [res.body] : [], uploadFileName, { type: 'text/octet-stream' });
    } else {
      file = this.blobToFile(new Blob(res ? [res.body] : [], { type: 'text/octet-stream' }), uploadFileName);
    }
    return file;
  }
  /**
   * Get the identifier of the object in edition if it is provided
   * in the parent context or in the URL as path parameter
   * @param parentCtx
   * @param params
   * @returns {string}
   */
  getCode(parentCtx: any, params: any) {
    return parentCtx && parentCtx.code
      ? parentCtx.code
      : this.getParamCode(params);
  }
  /**
   * Get the identifier from the params provided as input
   * @param params
   * @returns {string}
   */
  private getParamCode(params: any) {
    const id = params.id;
    return id && !id.startsWith('{') ? id : undefined;
  }
  /**
   * Gets the component's state context
   * @param feature
   * @param component
   * @param id
   * @param parentCtx
   * @param ignoreDispatchingSelectionByRole
   * @returns {ScreenContext}
   */
  getContext(feature: string, component: string, id: string, parentCtx?: any, ignoreDispatchingSelectionByRole?: boolean) {
    return {
      ...buildScreenCtx(
        feature,
        component,
        id,
        parentCtx ? parentCtx.roleName : undefined,
        parentCtx ? parentCtx.rootId : undefined,
        parentCtx && parentCtx.roleName
          ? id + ',' + parentCtx.screenId
          : undefined), ignoreDispatchingSelectionByRole
    };
  }
  /**
   * Converts Blob object to File object
   * @param blob
   * @param fileName
   * @return {File}
   */
  blobToFile(blob: Blob, fileName: string): File {
    let file: any = blob;
    file.lastModifiedDate = new Date();
    file.name = fileName;
    return <File>file;
  }
  /**
    * Gets the action's payload to fire search action in a virtual lazy table
    * @param payload
    * @param pageSize
    * @param first
    * @param totalItems
    * @param orderKeys
    */
  getVirtualLazyPayload(payload: any, pageSize: number, page: number, orderKeys = undefined) {
    const actionPayload = { ...payload };
    actionPayload['vars']['page'] = page;
    actionPayload['vars']['size'] = pageSize;
    if (orderKeys) {
      actionPayload['order'] = orderKeys;
    }
    return actionPayload;
  }
  /**
   * Gets the screen's identifier
   * @param ctx
   * @param rootId
   * @param screenId
   * @param roleName
   * @returns {string}
   */
  getScreenId(ctx: ScreenContext, rootId: string, screenId: string, roleName: string = undefined) {
    this.context[screenId] = {
      ...ctx,
      rootId,
      roleName
    };
    return screenId;
  }
  /**
   * Gets the query parameter screen's identifier
   * @param ctx
   * @returns {string}
   */
  getQueryScreenId(ctx: ScreenContext): string | number {
    this.context[ctx.screenId] = {
      ...ctx
    };
    return ctx.screenId;
  }
  /**
   * Gets the parent context
   * @param screenId
   * @returns {ScreenContext}
   */
  getParentContext(screenId: string): ScreenContext {
    return this.context[screenId] || this.context[this.ctxQueryParam];
  }
  /**
   * Clear the context
   * @param screenId
   */
  clearContext(screenId: string): void {
    this.context={};
  }
  /**
  * Sets the focus to the provided native element
  * @param element
  * @param value
  */
  setElementFocus(element, value) {
    if (!element.focused && (element.hasOwnProperty('applyFocus') || element.filter)) {
      element.focus();
    } else if (!element.focused || !element.focus) {
      this.setFocus(element, value);
    }
  }
  /**
  * Sets the focus to the provided native element
  * @param element
  * @param value
  */
  setFocus(element, value) {
    (element?.nativeElement?.querySelector(value) as HTMLElement).setAttribute('tabindex', '0');
    (element?.nativeElement?.querySelector(value) as HTMLElement).focus();
  }

  /**
  * Format file size
  * @param bytes
  */
   formatSize(bytes) {
    if (bytes == 0) {
        return '0 B';
    }
    let k = 1024,
    dm = 3,
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
  transformDateForFormControl(value){
    if(value){
      return  new Date(Date.UTC(value.getFullYear(), value.getMonth(),value.getDate(),0, 0, 0));
    }else{
      return value
    }
   
  }
  rTransformDateForFormControl(value){
    if(value){
      return   moment(value).toDate();
    }else{
      return value
    }
  }

}
